import { clients } from '@moovly/clients';
import { Organization } from '@moovly/clients/lib/resources';
import { GroupInfo } from '@moovly/clients/lib/resources/group/group.interfaces';
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import accessSlice from '../../../store/access.store';
import {
  DefaultApiRequestState,
  getDefaultApiRequestState,
  getDefaultReducerForAsyncRequest,
  RequestType,
} from '../../../store/default';

type OrganizationState = {
  groups: DefaultApiRequestState<GroupInfo[], [RequestType.Post, RequestType.Put, RequestType.Delete]>;
};

const initialState: OrganizationState = {
  groups: getDefaultApiRequestState([RequestType.Post, RequestType.Put, RequestType.Delete]),
};

export const createGroup = createAsyncThunk<Organization, { name: string; organizationId: string }>(
  'dashboard/organization/createGroup',
  ({ name, organizationId }, { dispatch }) => {
    return clients.Organization.createGroup(organizationId, { name }).then((group) => {
      dispatch(accessSlice.actions.addGroup(group));
      return group;
    });
  }
);

export const editGroup = createAsyncThunk<Organization, { name: string; groupId: string }>(
  'dashboard/organization/editGroup',
  ({ name, groupId }) => {
    return clients.Organization.patchGroup(groupId, { name });
  }
);

export const deleteGroup = createAsyncThunk<{ id: string }, { groupId: string }>(
  'dashboard/organization/deleteGroup',
  async ({ groupId }) => {
    await clients.Organization.deleteGroup(groupId);
    return { id: groupId };
  }
);

export const organizationSlice = createSlice({
  name: 'dashboard/organization',
  initialState,
  reducers: {
    reset: () => initialState,
    setGroups: (state, { payload }: PayloadAction<GroupInfo[]>) => {
      state.groups.data = payload;
    },
  },
  extraReducers: {
    ...getDefaultReducerForAsyncRequest({
      thunk: createGroup,
      requestType: RequestType.Post,
      getInitialState: (state) => state.groups,
      onSuccess: (state, payload) => {
        if (!state.data.some((d: GroupInfo) => d.id !== payload.id)) {
          state.data.push(payload);
        }
      },
    }),
    ...getDefaultReducerForAsyncRequest({
      thunk: editGroup,
      requestType: RequestType.Put,
      getInitialState: (state) => state.groups,
      onSuccess: (state, payload) => {
        const index = state.data.findIndex((gr: GroupInfo) => gr.id === payload.id);
        state.data[index] = payload;
      },
    }),
    ...getDefaultReducerForAsyncRequest({
      thunk: deleteGroup,
      requestType: RequestType.Delete,
      getInitialState: (state) => state.groups,
      onSuccess: (state, payload) => {
        state.data = state.data.filter((d: GroupInfo) => d.id !== payload.id);
      },
    }),
  },
});

export default organizationSlice;
